import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, RefreshCw, Check, X, Archive, ExternalLink, Loader2, ChevronLeft, ChevronRight, Trash2, Image, Tag, Star, Truck, Zap, Package } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const SOURCE_LABELS = {
    promobit: { label: 'Promobit', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)' },
    kabum: { label: 'KaBuM', color: '#FF6500', bg: 'rgba(255,101,0,0.1)' },
    mercadolivre: { label: 'Mercado Livre', color: '#FFE600', bg: 'rgba(255,230,0,0.1)' },
    amazon: { label: 'Amazon', color: '#FF9900', bg: 'rgba(255,153,0,0.1)' },
    magalu: { label: 'Magalu', color: '#0086FF', bg: 'rgba(0,134,255,0.1)' },
    americanas: { label: 'Americanas', color: '#E60014', bg: 'rgba(230,0,20,0.1)' },
};
const STATUS_MAP = {
    new: { label: 'Nova', cls: 'sv-badge-gold' },
    review: { label: 'Revisao', cls: 'sv-badge-warning' },
    approved: { label: 'Aprovada', cls: 'sv-badge-success' },
    ignored: { label: 'Ignorada', cls: 'sv-badge-muted' },
    published: { label: 'Publicada', cls: 'sv-badge-success' },
    duplicate: { label: 'Duplicada', cls: 'sv-badge-muted' },
};

export default function DiscoveriesPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [crawling, setCrawling] = useState(false);
    const [stats, setStats] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterSource, setFilterSource] = useState('');
    const [selected, setSelected] = useState(new Set());
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: page.toString(), limit: '30' });
            if (filterStatus) params.set('status', filterStatus);
            if (filterSource) params.set('source', filterSource);
            const r = await fetch(`${API}/discoveries?${params}`, { headers });
            const d = await r.json();
            setItems(d.items || []);
            setTotal(d.total || 0);
            setPages(d.pages || 1);
        } catch { toast.error('Erro ao carregar descobertas'); }
        finally { setLoading(false); }
    }, [page, filterStatus, filterSource]);

    const fetchStats = async () => {
        try {
            const r = await fetch(`${API}/crawler/stats`, { headers });
            setStats(await r.json());
        } catch {}
    };

    useEffect(() => { fetchItems(); }, [fetchItems]);
    useEffect(() => { fetchStats(); }, []);

    const handleCrawl = async (source = 'all') => {
        setCrawling(true);
        try {
            const r = await fetch(`${API}/crawler/run`, { method: 'POST', headers, body: JSON.stringify({ source }) });
            const d = await r.json();
            toast.success(d.message || 'Rastreamento concluido');
            fetchItems();
            fetchStats();
        } catch { toast.error('Erro ao rastrear'); }
        finally { setCrawling(false); }
    };

    const updateStatus = async (id, status) => {
        await fetch(`${API}/discoveries/${id}/status`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
        fetchItems();
        fetchStats();
    };

    const publishDisc = async (id) => {
        try {
            const r = await fetch(`${API}/discoveries/${id}/publish`, { method: 'POST', headers });
            const d = await r.json();
            if (d.deal_id) toast.success('Oferta publicada!');
            else toast.error(d.message || 'Erro');
            fetchItems();
            fetchStats();
        } catch { toast.error('Erro ao publicar'); }
    };

    const bulkAction = async (action) => {
        if (selected.size === 0) return toast.error('Selecione itens');
        await fetch(`${API}/discoveries/bulk-action`, { method: 'POST', headers, body: JSON.stringify({ ids: Array.from(selected), action }) });
        toast.success(`${selected.size} item(ns) atualizados`);
        setSelected(new Set());
        fetchItems();
        fetchStats();
    };

    const toggleSelect = (id) => setSelected(prev => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });
    const toggleAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map(i => i.discovery_id)));
    };

    return (
        <div className="sv-page" data-testid="discoveries-page">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Descobertas</h2>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => handleCrawl('all')} disabled={crawling} data-testid="crawl-now-btn">
                    {crawling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    <span className="ml-1.5">{crawling ? 'Rastreando...' : 'Rastrear agora'}</span>
                </button>
            </div>

            {/* Stats bar */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4" data-testid="crawler-stats">
                    <StatCard label="Total" value={stats.total_discoveries} />
                    <StatCard label="Novas" value={stats.by_status?.new || 0} color="#7DB3FF" />
                    <StatCard label="Aprovadas" value={stats.by_status?.approved || 0} color="#22c55e" />
                    <StatCard label="Publicadas" value={stats.by_status?.published || 0} color="#3b82f6" />
                    <StatCard label="Ignoradas" value={stats.by_status?.ignored || 0} color="#888" />
                    <StatCard label="Duplicadas" value={stats.config_stats?.total_duplicates || 0} color="#ef4444" />
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-1.5">
                    <Filter size={13} style={{ color: 'var(--muted)' }} />
                    <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                        className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-status">
                        <option value="">Todos os status</option>
                        <option value="new">Novas</option>
                        <option value="review">Revisao</option>
                        <option value="approved">Aprovadas</option>
                        <option value="ignored">Ignoradas</option>
                        <option value="published">Publicadas</option>
                    </select>
                    <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }}
                        className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-source">
                        <option value="">Todas as fontes</option>
                        {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                </div>
                {selected.size > 0 && (
                    <div className="flex items-center gap-1 ml-auto">
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>{selected.size} selecionado(s)</span>
                        <button className="sv-btn-system text-xs" style={{ padding: '3px 8px' }} onClick={() => bulkAction('approved')} data-testid="bulk-approve"><Check size={12} /> Aprovar</button>
                        <button className="sv-btn-system text-xs" style={{ padding: '3px 8px' }} onClick={() => bulkAction('ignored')} data-testid="bulk-ignore"><X size={12} /> Ignorar</button>
                        <button className="sv-btn-system text-xs" style={{ padding: '3px 8px' }} onClick={() => bulkAction('delete')} data-testid="bulk-delete"><Trash2 size={12} style={{ color: '#ef4444' }} /> Excluir</button>
                    </div>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>
            ) : items.length === 0 ? (
                <div className="sv-empty-state">
                    <Search size={40} style={{ color: 'var(--muted-2)' }} />
                    <p className="sv-empty-state-title">Nenhuma descoberta encontrada</p>
                    <p className="sv-empty-state-desc">Clique em "Rastrear agora" para buscar ofertas nas lojas configuradas</p>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} className="rounded" data-testid="select-all" />
                        <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{total} resultado(s) - Pagina {page}/{pages}</span>
                    </div>
                    <div className="flex flex-col gap-2" data-testid="discoveries-list">
                        {items.map(item => <DiscoveryCard key={item.discovery_id} item={item} selected={selected.has(item.discovery_id)}
                            onToggle={() => toggleSelect(item.discovery_id)} onApprove={() => updateStatus(item.discovery_id, 'approved')}
                            onIgnore={() => updateStatus(item.discovery_id, 'ignored')} onPublish={() => publishDisc(item.discovery_id)}
                            onReview={() => updateStatus(item.discovery_id, 'review')} />)}
                    </div>
                    {/* Pagination */}
                    {pages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <button className="sv-btn-system" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></button>
                            <span className="text-xs" style={{ color: 'var(--muted)' }}>{page} / {pages}</span>
                            <button className="sv-btn-system" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function StatCard({ label, value, color }) {
    return (
        <div className="sv-card-system text-center" style={{ padding: '10px 8px' }}>
            <div className="text-lg font-bold" style={{ color: color || 'var(--text)' }}>{value}</div>
            <div className="text-[10px]" style={{ color: 'var(--muted)' }}>{label}</div>
        </div>
    );
}

function DiscoveryCard({ item, selected, onToggle, onApprove, onIgnore, onPublish, onReview }) {
    const src = SOURCE_LABELS[item.source] || { label: item.source, color: '#888', bg: 'rgba(136,136,136,0.1)' };
    const st = STATUS_MAP[item.status] || { label: item.status, cls: 'sv-badge-muted' };
    const [imgErr, setImgErr] = React.useState(false);
    return (
        <div className="sv-card-system disc-card-mobile" style={{ padding: '10px 12px', opacity: item.status === 'ignored' ? 0.5 : 1 }} data-testid={`disc-${item.discovery_id}`}>
            {/* Top row: checkbox + badges */}
            <div className="flex items-center gap-2 mb-2">
                <input type="checkbox" checked={selected} onChange={onToggle} className="shrink-0 rounded" />
                <div className="flex items-center gap-1 flex-wrap min-w-0">
                    <span className={`sv-badge ${st.cls}`} style={{ fontSize: 9 }}>{st.label}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: src.bg, color: src.color }}>{src.label}</span>
                    {item.priority_score >= 50 && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }}>Score {item.priority_score}</span>}
                    {item.free_shipping && <Truck size={11} style={{ color: '#22c55e' }} title="Frete grátis" />}
                    {item.coupon && <Tag size={11} style={{ color: 'var(--sv-gold-primary)' }} title={`Cupom: ${item.coupon}`} />}
                </div>
            </div>
            {/* Content row: image + text */}
            <div className="flex gap-2.5">
                {item.image_url && !imgErr ? (
                    <img src={item.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" style={{ background: 'var(--surface-2)' }} onError={() => setImgErr(true)} />
                ) : (
                    <div className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}><Image size={18} style={{ color: 'var(--muted-2)' }} /></div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2 leading-snug" style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{item.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-sm font-bold" style={{ color: '#22c55e' }}>R$ {item.current_price?.toFixed(2)}</span>
                        {item.original_price > item.current_price && (
                            <span className="text-[10px] line-through" style={{ color: 'var(--muted-2)' }}>R$ {item.original_price?.toFixed(2)}</span>
                        )}
                        {item.discount_percent > 0 && (
                            <span className="text-[10px] font-bold px-1 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>-{item.discount_percent}%</span>
                        )}
                    </div>
                </div>
            </div>
            {/* Bottom row: action buttons + date */}
            <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="flex gap-1 flex-wrap">
                    {item.status !== 'approved' && item.status !== 'published' && (
                        <button className="sv-btn-system" style={{ padding: '4px 8px', fontSize: 11 }} onClick={onApprove} data-testid={`approve-${item.discovery_id}`}><Check size={12} style={{ color: '#22c55e' }} /> <span className="ml-0.5">Aprovar</span></button>
                    )}
                    {item.status !== 'ignored' && item.status !== 'published' && (
                        <button className="sv-btn-system" style={{ padding: '4px 8px', fontSize: 11 }} onClick={onIgnore} data-testid={`ignore-${item.discovery_id}`}><X size={12} style={{ color: '#ef4444' }} /> <span className="ml-0.5">Ignorar</span></button>
                    )}
                    {item.status === 'approved' && (
                        <button className="sv-btn-gold-system sv-btn-system" style={{ padding: '4px 8px', fontSize: 11 }} onClick={onPublish} data-testid={`publish-${item.discovery_id}`}><Zap size={11} /> <span className="ml-0.5">Publicar</span></button>
                    )}
                    {item.offer_url && (
                        <a href={item.offer_url} target="_blank" rel="noopener noreferrer" className="sv-btn-system" style={{ padding: '4px 8px' }}><ExternalLink size={12} /></a>
                    )}
                </div>
                <span className="text-[9px] shrink-0" style={{ color: 'var(--muted-2)' }}>{item.crawled_at ? new Date(item.crawled_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
            </div>
        </div>
    );
}
